import React, { useState, useEffect } from 'react';
import { crmApi, globalApi } from '../services/crmApi';
import {
  Users,
  TrendingUp,
  TrendingDown,
  Award,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Sparkles,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  BarChart2,
  Activity,
  RefreshCw,
  Banknote,
  CircleDollarSign,
  Receipt,
} from 'lucide-react';

interface DashboardData {
  applications: {
    total: number;
    byStatus: Record<string, number>;
    thisWeek: number;
    thisMonth: number;
  };
  contacts: {
    total: number;
    thisWeek: number;
    thisMonth: number;
  };
  assessments: {
    total: number;
    passed: number;
    avgScore: number;
  };
  ai: { totalAnalyses: number };
  sms: { totalSent: number };
  emails: { tracked: number };
  documents: { total: number };
  recentActivity: Array<{
    type: string;
    description: string;
    date: string;
  }>;
}

interface ChartData {
  weeklyApplications: Array<{ week: string; count: number }>;
  weeklyContacts: Array<{ week: string; count: number }>;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  new_application: {
    label: 'Nowe',
    color: 'text-blue-800',
    bg: 'bg-blue-100',
    icon: <Clock size={14} />,
  },
  initial_verification_ok: {
    label: 'Weryfikacja OK',
    color: 'text-green-800',
    bg: 'bg-green-100',
    icon: <CheckCircle size={14} />,
  },
  warning_risk: {
    label: 'Ryzyko',
    color: 'text-yellow-800',
    bg: 'bg-yellow-100',
    icon: <AlertTriangle size={14} />,
  },
  ready_to_write: {
    label: 'Do pisania',
    color: 'text-purple-800',
    bg: 'bg-purple-100',
    icon: <FileText size={14} />,
  },
  contract_signed: {
    label: 'Umowa',
    color: 'text-indigo-800',
    bg: 'bg-indigo-100',
    icon: <FileText size={14} />,
  },
  application_submitted: {
    label: 'Zlozony',
    color: 'text-cyan-800',
    bg: 'bg-cyan-100',
    icon: <Mail size={14} />,
  },
  success: {
    label: 'Sukces',
    color: 'text-green-800',
    bg: 'bg-green-100',
    icon: <Award size={14} />,
  },
  rejected: {
    label: 'Odrzucony',
    color: 'text-red-800',
    bg: 'bg-red-100',
    icon: <TrendingDown size={14} />,
  },
};

const ACTIVITY_ICONS: Record<string, { icon: React.ReactNode; color: string }> = {
  application: { icon: <FileText size={16} />, color: 'bg-blue-100 text-blue-600' },
  contact: { icon: <MessageSquare size={16} />, color: 'bg-green-100 text-green-600' },
  assessment: { icon: <Target size={16} />, color: 'bg-purple-100 text-purple-600' },
};

interface CRMDashboardProps {
  productCode?: string;
}

export const CRMDashboard: React.FC<CRMDashboardProps> = ({ productCode }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [finStats, setFinStats] = useState<{ count: number; totalGross: number; paidGross: number; unpaidGross: number } | null>(null);
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAll();
  }, [productCode]);

  const loadAll = async () => {
    try {
      const [dashData, chartData, finData, clientData] = await Promise.all([
        crmApi.getDashboardStats(productCode),
        crmApi.getDashboardChart(productCode),
        globalApi.getFinancialStats().catch(() => null),
        globalApi.getClientDashboardStats().catch(() => null),
      ]);
      setData(dashData);
      setChart(chartData);
      // Extract financial stats for this product
      if (finData?.byServiceType && productCode) {
        const match = finData.byServiceType.find((s: any) => s.serviceType === productCode);
        setFinStats(match || null);
      } else {
        setFinStats(null);
      }
      // Extract client count for this product
      if (clientData?.byProduct && productCode) {
        const cm = clientData.byProduct.find((p: any) => p.tag === productCode);
        setClientCount(cm ? cm.count : 0);
      } else {
        setClientCount(null);
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadAll();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <RefreshCw className="animate-spin mx-auto text-blue-500 mb-3" size={32} />
        <div className="text-gray-500">Ładowanie dashboardu...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-red-500 mb-2">Błąd ładowania statystyk</div>
        <button onClick={handleRefresh} className="text-blue-600 hover:underline text-sm">
          Sprobuj ponownie
        </button>
      </div>
    );
  }

  const chartMax = Math.max(
    ...(chart?.weeklyApplications?.map(w => w.count) || [1]),
    ...(chart?.weeklyContacts?.map(w => w.count) || [1]),
    1
  );

  return (
    <div className="space-y-6">
      {/* Refresh button */}
      <div className="flex justify-end">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 transition-colors"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Odświeżanie...' : 'Odswiez'}
        </button>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={<Users size={28} />}
          label="Wnioski"
          value={data.applications.total}
          subtitle={`+${data.applications.thisWeek} ten tydzien`}
          gradient="from-blue-500 to-blue-600"
          subtitleColor="text-blue-100"
        />
        <KPICard
          icon={<MessageSquare size={28} />}
          label="Kontakty"
          value={data.contacts.total}
          subtitle={`+${data.contacts.thisWeek} ten tydzien`}
          gradient="from-green-500 to-emerald-600"
          subtitleColor="text-green-100"
        />
        <KPICard
          icon={<Target size={28} />}
          label="Testy"
          value={data.assessments.total}
          subtitle={`${data.assessments.passed} pozytywnych`}
          gradient="from-purple-500 to-purple-600"
          subtitleColor="text-purple-100"
        />
        <KPICard
          icon={<Award size={28} />}
          label="Sredni wynik"
          value={data.assessments.avgScore}
          subtitle="punktow (test)"
          gradient="from-orange-500 to-orange-600"
          subtitleColor="text-orange-100"
        />
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniStat icon={<Sparkles size={18} />} label="Analizy AI" value={data.ai.totalAnalyses} color="text-purple-600" bg="bg-purple-50" />
        <MiniStat icon={<Phone size={18} />} label="SMS wysłane" value={data.sms.totalSent} color="text-green-600" bg="bg-green-50" />
        <MiniStat icon={<Mail size={18} />} label="Emaile" value={data.emails.tracked} color="text-blue-600" bg="bg-blue-50" />
        <MiniStat icon={<FileText size={18} />} label="Dokumenty" value={data.documents.total} color="text-orange-600" bg="bg-orange-50" />
        {clientCount !== null && <MiniStat icon={<Users size={18} />} label="Klienci w bazie" value={clientCount} color="text-slate-600" bg="bg-slate-50" />}
      </div>

      {/* Financial KPIs (from Fakturownia) */}
      {finStats && finStats.count > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Banknote className="mr-2 text-green-600" size={22} />
            Finanse (Fakturownia)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <div className="flex items-center gap-1.5 mb-1">
                <CircleDollarSign size={14} className="text-green-500" />
                <span className="text-xs text-gray-500">Przychód brutto</span>
              </div>
              <div className="text-xl font-bold text-green-700">
                {finStats.totalGross.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} zł
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle size={14} className="text-blue-500" />
                <span className="text-xs text-gray-500">Opłacone</span>
              </div>
              <div className="text-xl font-bold text-blue-700">
                {finStats.paidGross.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} zł
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock size={14} className="text-red-500" />
                <span className="text-xs text-gray-500">Nieopłacone</span>
              </div>
              <div className="text-xl font-bold text-red-700">
                {finStats.unpaidGross.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} zł
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <Receipt size={14} className="text-gray-500" />
                <span className="text-xs text-gray-500">Faktur łącznie</span>
              </div>
              <div className="text-xl font-bold text-gray-700">{finStats.count}</div>
            </div>
          </div>
        </div>
      )}

      {/* Status Breakdown + Weekly Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <CheckCircle className="mr-2 text-blue-600" size={22} />
            Wnioski wg statusu
          </h3>
          <div className="space-y-2">
            {Object.entries(data.applications.byStatus).map(([status, count]) => {
              const cfg = STATUS_CONFIG[status] || { label: status, color: 'text-gray-800', bg: 'bg-gray-100', icon: null };
              const pct = data.applications.total > 0 ? (count / data.applications.total) * 100 : 0;

              return (
                <div key={status} className="flex items-center gap-3">
                  <div className={`${cfg.bg} ${cfg.color} px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 min-w-[120px]`}>
                    {cfg.icon}
                    {cfg.label}
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all ${cfg.bg.replace('100', '400')}`}
                      style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: getStatusBarColor(status) }}
                    />
                  </div>
                  <span className="text-sm font-bold text-gray-700 min-w-[30px] text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Weekly Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <BarChart2 className="mr-2 text-indigo-600" size={22} />
            Aktywność (ostatnie 8 tygodni)
          </h3>
          {chart && chart.weeklyApplications?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-end gap-2 h-40">
                {chart.weeklyApplications.map((week, i) => {
                  const contactCount = chart.weeklyContacts?.[i]?.count || 0;
                  const appHeight = (week.count / chartMax) * 100;
                  const contactHeight = (contactCount / chartMax) * 100;
                  const weekLabel = week.week.split('-W')[1] ? `T${week.week.split('-W')[1]}` : week.week;

                  return (
                    <div key={week.week} className="flex-1 flex flex-col items-center gap-1">
                      <div className="flex items-end gap-0.5 h-32 w-full justify-center">
                        <div
                          className="bg-blue-400 rounded-t w-3 transition-all hover:bg-blue-500"
                          style={{ height: `${Math.max(appHeight, 4)}%` }}
                          title={`Wnioski: ${week.count}`}
                        />
                        <div
                          className="bg-green-400 rounded-t w-3 transition-all hover:bg-green-500"
                          style={{ height: `${Math.max(contactHeight, 4)}%` }}
                          title={`Kontakty: ${contactCount}`}
                        />
                      </div>
                      <span className="text-[10px] text-gray-400">{weekLabel}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 bg-blue-400 rounded" /> Wnioski
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 bg-green-400 rounded" /> Kontakty
                </span>
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
              Brak danych za ostatnie 8 tygodni
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Activity className="mr-2 text-green-600" size={22} />
            Ostatnia aktywnosc
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {data.recentActivity?.length > 0 ? (
              data.recentActivity.map((item, i) => {
                const actCfg = ACTIVITY_ICONS[item.type] || { icon: <Clock size={16} />, color: 'bg-gray-100 text-gray-600' };
                const timeAgo = getTimeAgo(item.date);

                return (
                  <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                    <div className={`${actCfg.color} p-2 rounded-full flex-shrink-0`}>
                      {actCfg.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{item.description}</p>
                      <p className="text-xs text-gray-400">{timeAgo}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-400 text-sm py-4">
                Brak ostatniej aktywnosci
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold mb-4 text-blue-900 flex items-center">
            <TrendingUp className="mr-2" size={22} />
            Szybkie podsumowanie
          </h3>
          <div className="grid grid-cols-1 gap-3">
            <QuickAction
              label="Nowe wnioski"
              value={data.applications.byStatus?.new_application || 0}
              color="text-blue-600"
              note="Wymagaja weryfikacji"
            />
            <QuickAction
              label="Ostrzezenia"
              value={data.applications.byStatus?.warning_risk || 0}
              color="text-yellow-600"
              note="Wymagaja uwagi"
            />
            <QuickAction
              label="Gotowe do pisania"
              value={data.applications.byStatus?.ready_to_write || 0}
              color="text-purple-600"
              note="Mozna generowac wnioski"
            />
            <QuickAction
              label="Ten miesiac"
              value={data.applications.thisMonth}
              color="text-green-600"
              note="Nowych wnioskow w tym miesiacu"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Helper Components ────────────────────────────────────

function KPICard({ icon, label, value, subtitle, gradient, subtitleColor }: {
  icon: React.ReactNode;
  label: string;
  value: number;
  subtitle: string;
  gradient: string;
  subtitleColor: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} text-white rounded-lg shadow-lg p-5`}>
      <div className="flex items-center justify-between mb-2">
        {icon}
        <span className={`${subtitleColor} text-xs font-medium`}>{label}</span>
      </div>
      <div className="text-3xl font-bold mb-0.5">{value}</div>
      <div className={`${subtitleColor} text-sm`}>{subtitle}</div>
    </div>
  );
}

function MiniStat({ icon, label, value, color, bg }: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  bg: string;
}) {
  return (
    <div className={`${bg} rounded-lg p-4 flex items-center gap-3`}>
      <div className={color}>{icon}</div>
      <div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function QuickAction({ label, value, color, note }: {
  label: string;
  value: number;
  color: string;
  note: string;
}) {
  return (
    <div className="bg-white rounded-lg p-3 shadow-sm flex items-center justify-between">
      <div>
        <div className="text-sm font-medium text-gray-700">{label}</div>
        <div className="text-xs text-gray-400">{note}</div>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

// ── Helper Functions ─────────────────────────────────────

function getStatusBarColor(status: string): string {
  const colors: Record<string, string> = {
    new_application: '#3b82f6',
    initial_verification_ok: '#22c55e',
    warning_risk: '#eab308',
    ready_to_write: '#a855f7',
    contract_signed: '#6366f1',
    application_submitted: '#06b6d4',
    success: '#16a34a',
    rejected: '#ef4444',
  };
  return colors[status] || '#9ca3af';
}

function getTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Przed chwila';
  if (minutes < 60) return `${minutes} min temu`;
  if (hours < 24) return `${hours} godz. temu`;
  if (days === 1) return 'Wczoraj';
  if (days < 7) return `${days} dni temu`;
  return date.toLocaleDateString('pl-PL');
}
